// Interesting take on isolated vs redux components: https://medium.com/netscape/component-state-vs-redux-store-1eb0c929277
// Slow rendering: https://github.com/amcharts/amcharts4/issues/723
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import _ from 'lodash';

import pushToGTM, { EXPORT_CHART_GA4_TAG } from '../../../utils/analytics/gtm';
import { COLORS, FONT_SIZES, CN_FONT_SIZES } from '../../../styles/constants';
import { CHARTS, VIN_VOLTAGE_IN_GRAPHS } from '../constants';
import { getDomain } from '../../../utils/config';
import { getStrForInputParams } from '../utils';

const FONT_SIZE = getDomain() === 'onsemi.com' ? FONT_SIZES : CN_FONT_SIZES;

class LineAmChart extends Component {
	componentDidMount() {
		this.initChart();
	}

	componentDidUpdate(prevProps) {
		// Handle refreshing the chart when the dataset changes
		if (!_.isEqual(prevProps.data, this.props.data)) {
			if (this.chart) {
				this.chart.dispose();
			}

			this.initChart();
		}
	}

	componentWillUnmount() {
		if (this.chart) {
			this.chart.dispose();
		}
	}

	initChart() {
		const { designId, data, inputParams } = this.props;
		// Am4core.useTheme(am4themesAnimated);
		am4core.options.commercialLicense = true;

		this.chart = am4core.create(data.chartName, am4charts.XYChart);

		this.chart.colors.list = [
			am4core.color(COLORS.PRIMARY),
			am4core.color(COLORS.LINK),
			am4core.color(COLORS.BACKGROUND_GREY_DARK),
			am4core.color('green'),
			am4core.color('red'),
			am4core.color('yellow'),
			am4core.color('purple')
		];

		let chartConst = CHARTS[data.chartName];

		if (!chartConst) {
			chartConst = {
				key: data.chartName,
				menuLabel: data.chartName,
				title: data.chartName,
				xAxisTitle: '',
				xAxisTitleForData: '',
				yAxisTitle: '',
				seriesNames: {}
			};
		}

		/* X axis */
		const xAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
		xAxis.fontSize = FONT_SIZE.BODY2;
		xAxis.title.text = chartConst.xAxisTitle;
		xAxis.title.fontSize = FONT_SIZE.BODY1;
		xAxis.title.paddingBottom = 10;
		xAxis.tooltip.fontSize = FONT_SIZE.BODY2;
		xAxis.renderer.minGridDistance = 50;
		xAxis.renderer.grid.template.location = 0.5;
		xAxis.dataFields.category = 'x';
		xAxis.data = data.series.length > 0 ? data.series[0].items : [];

		/* Y axis */
		const yAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
		yAxis.fontSize = FONT_SIZE.BODY2;
		yAxis.title.text = chartConst.yAxisTitle;
		yAxis.title.fontSize = FONT_SIZE.BODY1;
		yAxis.tooltip.fontSize = FONT_SIZE.BODY2;

		for (let i = 0; i < data.series.length; ++i) {
			const series = this.chart.series.push(new am4charts.LineSeries());
			series.showOnInit = false; // ?
			series.tensionX = 0.9;
			series.tensionY = 0.92;
			series.dataFields.valueY = 'y';
			series.dataFields.categoryX = 'x';
			series.name = chartConst.seriesNames[data.series[i].seriesName]
				? VIN_VOLTAGE_IN_GRAPHS.includes(data.series[i].seriesName)
					? chartConst.seriesNames[data.series[i].seriesName] +
					  '[font-size: 13]' +
					  ' = ' +
					  inputParams[getStrForInputParams(data.series[i].seriesName)] +
					  ' V [/]'
					: chartConst.seriesNames[data.series[i].seriesName]
				: '[font-size: 13]' + ' ' + data.series[i].seriesName + '[/]';
			series.data = data.series[i].items;
			series.strokeWidth = 2;
			series.tooltipText = '[font-size: 13;]{valueY}[/]';
			series.tooltip.background.cornerRadius = 1;
			series.tooltip.background.strokeOpacity = 0;

			/* Add bullet if only one value is returned from calc */
			if (data.series[i] && data.series[i].items.length === 1) {
				const bullet = series.bullets.push(new am4charts.CircleBullet());
				bullet.circle.radius = 2;
			}
		}

		/* Legend */
		this.chart.legend = new am4charts.Legend();
		this.chart.legend.useDefaultMarker = true;
		this.chart.numberFormatter.numberFormat = '.##a';
		const marker = this.chart.legend.markers.template.children.getIndex(0);
		marker.cornerRadius(1.2, 1.2, 1.2, 1.2);
		marker.width = 15;
		marker.height = 15;

		/* Cursor */
		this.chart.cursor = new am4charts.XYCursor();
		this.chart.cursor.lineX.strokeOpacity = 0.25;
		this.chart.cursor.lineY.strokeOpacity = 0.25;

		/* Title */
		const title = this.chart.titles.create();
		title.text = chartConst.title;
		title.fontSize = FONT_SIZE.SUBTITLE2;
		title.marginBottom = 30;

		/* Zoom out button */
		this.chart.zoomOutButton.background.cornerRadius(1.2, 1.2, 1.2, 1.2);
		this.chart.zoomOutButton.background.fill = am4core.color(COLORS.PRIMARY);
		this.chart.zoomOutButton.icon.stroke = am4core.color('rgb(250,250,250)');
		this.chart.zoomOutButton.icon.strokeWidth = 2;
		this.chart.zoomOutButton.background.states.getKey('hover').properties.fill = am4core.color(COLORS.PRIMARY);
		this.chart.zoomOutButton.background.states.getKey('down').properties.fill = am4core.color(
			COLORS.PRIMARY2
		); /* This.chart.exporting.menu.items[0].menu.push({
            label: 'Hello',
            type: 'custom',
            options: {
                callback: function() {
                    console.log('Hello back at ya!');
                },
            },
        }); */

		/* Exporting */
		this.chart.exporting.filePrefix = 'WebDesigner+ ' + designId + ' chart - ' + chartConst.menuLabel;
		this.chart.exporting.menu = new am4core.ExportMenu();
		this.chart.exporting.menu.closeDelay = 500;
		this.chart.exporting.menu.items[0].label = '';
		this.chart.exporting.menu.items[0].icon =
			'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgBAMAAAAQtmoLAAAAHlBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAC3KG9qAAAACnRSTlMA/0sEDFdvq8OPbB5ywwAAAMVJREFUeAHt0YEFwwAYROGoKAE8SHWLjBFAR0hnyATdIN245HBBm0MbqP8AeD5cc+xqNYBfBRVUUEEFFZyGbdDeYnCft8HYN2FnLoOD9sGUAJgdjNAngJUAWAGYErASACuQiCciAARw3Q06RAAIYNk5ygTAFgB4E5gAMPAxMAFgYC8Q4S0xEAEgIAYiDORAhIEciBAQAxMGctAZyIEJAznoDOTAhIEcdAJyYEJADkwYiIEIAzEQYSAH2p8GFeQdH1RQq329F+enTePQEFWtAAAAAElFTkSuQmCC';
		this.chart.exporting.events.on('exportstarted', _ev => {
			pushToGTM(EXPORT_CHART_GA4_TAG);
		});
		this.chart.exporting.adapter.add('data', () => {
			const chartData = [];

			if (this.chart.series.length > 0) {
				for (let index = 0; index < data.series[0].items.length; index++) {
					if (data.series[0].items[index].y != null) {
						chartData.push({
							[chartConst.xAxisTitleForData]: data.series[0].items[index].x
						});
					}
				}

				for (let i = 0; i < data.series.length; i++) {
					const seriesNameForData = VIN_VOLTAGE_IN_GRAPHS.includes(data.series[i].seriesName)
						? chartConst.yAxisTitleForData +
						  '(' +
						  chartConst.seriesNames[data.series[i].seriesName + 'Exp'] +
						  ' = ' +
						  inputParams[getStrForInputParams(data.series[i].seriesName)] +
						  ' V)'
						: chartConst.yAxisTitleForData + '(' + chartConst.seriesNames[data.series[i].seriesName + 'Exp'] + ')';

					let j = 0;
					if (data.series[i].items[j].y == null) {
						j = 1;
						while (j < data.series[i].items.length) {
							chartData[j - 1][seriesNameForData] = data.series[i].items[j].y;
							j++;
						}
					} else {
						while (j < data.series[i].items.length) {
							chartData[j][seriesNameForData] = data.series[i].items[j].y;
							j++;
						}
					}
				}
			}

			return { data: chartData };
		});

		/* Log/Linear scale switch button */
		/* let button = this.chart.chartContainer.topParent.createChild(
            am4core.Button
        );
        button.align = 'right';
        button.padding(9, 9, 9, 9);
        button.marginRight = 47;
        button.events.on('hit', function() {
            alert('cav');
        });
        button.icon = new am4core.Sprite();
        button.icon.path =
            'M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z';
        button.background.fill = am4core.color('#ffffff');
        button.background.states.getKey(
            'hover'
        ).properties.fill = am4core.color('#EBEBEB');
        button.background.states.getKey('down').properties.fill = am4core.color(
            AppConst.COLOR_GREY_1
        ); */
	}

	render() {
		return (
			<div
				id={this.props.data.chartName}
				className="linechart-container"
				style={{
					width: '100%',
					height: '430px',
					fontFamily: 'Inter'
				}}
			/>
		);
	}
}

LineAmChart.propTypes = {
	designId: PropTypes.number.isRequired,
	data: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default LineAmChart;
